import { TaskService } from 'src/app/core/service/task.service';
import { Component, OnInit, Input, Output, EventEmitter, OnChanges, ElementRef, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { User } from 'src/app/user/model/user';
import { sortBy, cloneDeep } from 'lodash';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Task } from 'src/app/case/model/task';
import { SettingService } from 'src/app/core/service/setting.service';
import { DocumentService } from 'src/app/core/service/document.service';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { CustomFile } from 'src/app/message/model/custom-file';
import { AssessmentListService } from 'src/app/core/service/grid-data/grid-data-assessment.service';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { ChannelService } from 'src/app/core/service/channel.service';
import { CaseService } from 'src/app/core/service/case.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
  selector: 'app-task-add',
  templateUrl: './task-add.component.html',
  styleUrls: ['./task-add.component.scss']
})
export class TaskAddComponent implements OnInit, OnChanges, OnDestroy
{
  @Input() public channelId: number = null;
  @Input() public caseId: number = null;
  @Input() public memberId: number = null;
  @Input() public eventId: number = null;
  @Input() public assigneeId: number = null;
  @Input() public allowAssigneeOverride = false;
  @Input() public followUpTaskId: number = null;

  @Output() public cancelling = new EventEmitter();
  @Output() public save = new EventEmitter();

  public taskForm: any;
  public userMember: User;
  public task: Task;
  public assigneeUser: User;
  public assigneeUsers: User[] = [];
  public assigneeUsersFiltered: User[] = [];
  public listPriorities: Array<{ text: string; id: string; value: number }>;
  public dialogAddTaskOpened = false;
  public minOpenedOn: Date = moment().subtract('1', 'year').toDate();
  public maxOpenedOn: Date;
  public selectedFiles: CustomFile[] = [];
  public fileRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.pdf', '.doc', '.docx'],
      maxFileSize: 2000
    };

  public assessments: any;
  public assessmentsFiltered = [];
  public addingTask = false;
  public attachmentsEnabled = false;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  private listSubscription: Subscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private taskListService: TaskService,
    private messageHandlerService: MessageHandlerService,
    private userService: UserService,
    private channelService: ChannelService,
    private settingService: SettingService,
    private elem: ElementRef,
    private documentService: DocumentService,
    private caseService: CaseService,
    private authenticationService: AuthenticationService,
    private assessmentListService: AssessmentListService)
  {
  }

  public get name(): UntypedFormControl
  {
    return this.taskForm.get('name');
  }

  public get openedOn(): UntypedFormControl
  {
    return this.taskForm.get('openedOn');
  }

  public get dueDate(): UntypedFormControl
  {
    return this.taskForm.get('dueDate');
  }

  public get priority(): UntypedFormControl
  {
    return this.taskForm.get('priority');
  }

  public get assignee(): UntypedFormControl
  {
    return this.taskForm.get('assignee');
  }

  public get assessment(): UntypedFormControl
  {
    return this.taskForm.get('assessment');
  }

  public get note(): UntypedFormControl
  {
    return this.taskForm.get('note');
  }

  @Input() public set dialogOpened(value: boolean)
  {
    if (value)
    {
      this.getAssessments();
      this.buildForm();
      document.body.style.overflow = 'hidden';
    }
    this.dialogAddTaskOpened = value;
  }

  public ngOnInit(): void
  {
    this.listPriorities = this.taskListService.listPriorities;
  }

  public ngOnChanges(): void
  {
    if (this.channelId == null && this.caseId !== null)
    {
      this.caseService.getCaseDetails(this.caseId, 'OPEN').subscribe({
        next: (aCase) =>
        {
          this.channelId = aCase.channelId;
          this.getTaskStaff();
        }
      });
    }
    else
    {
      this.getTaskStaff();
    }
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public assessmentItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Filter handler for task manager assign dropdown.
   *
   * @param value User input.
   */
  public filterAssigneeUsers(value: string): void
  {
    this.searchTerm = value;
    this.assigneeUsersFiltered = sortBy(this.assigneeUsers
      .filter(p => p.name.toLowerCase().includes(this.searchTerm.toLowerCase())), ['name']);
  }

  /**
   * Cancel the new ad-hoc task.
   */
  public cancelAdd(): void
  {
    // Close dialog
    this.cancelling.emit();
    document.body.style.overflow = '';
  }

  /**
   * Save the new ad-hoc task.
   */
  public createTask(): void
  {
    this.addingTask = true;

    if (this.listPriorities.length === 2)
    {
      this.updatePriority();
    }

    this.task = this.taskForm.value;
    this.task.eventId = this.eventId;
    this.task.followUpTaskId = this.followUpTaskId;
    this.task.memberId = this.memberId;
    this.task.caseId = this.caseId;
    this.task.channelId = this.channelId;
    this.task.priority = this.priority.value.id;
    this.task.files = this.selectedFiles;

    const openedOn = this.openedOn.value;
    const now = new Date();

    // Not the best since we allow openedOn date in the past and control not selecting time.
    // At least the time will be set to current.
    this.task.openedOn = new Date(openedOn.getFullYear(), openedOn.getMonth(), openedOn.getDate(),
      now.getHours(), now.getMinutes(), now.getSeconds());

    if (this.task.assignee)
    {
      this.task.assignee = this.task.assignee.username;
    }
    else if (this.assigneeUser !== undefined)
    {
      this.task.assignee = this.assigneeUser.username;
    }

    if (this.assessment !== undefined && this.assessment.value != null)
    {
      this.task.assessmentId = this.assessment.value.id;
    }
    else
    {
      this.task.assessmentId = null;
    }
    delete this.task.assessment;

    if (this.note !== undefined && this.note.value != null)
    {
      this.task.note = this.note.value;
    }
    this.saveTask();
    document.body.style.overflow = '';
  }

  public saveTask(): void
  {
    this.taskListService.saveTask(this.task).subscribe({
      next: data =>
      {
        this.messageHandlerService.displaySuccess('TASK-SAVED-SUCCESSFULLY');
        this.save.emit({ id: data.id, eventId: this.eventId });
      },
      error: () =>
      {
        this.messageHandlerService.displayError('TASK-SAVE-ERROR');
        this.addingTask = false;
      },
      complete: () =>
      {
        this.addingTask = false;
      }
    });
  }

  public updatePriority(): void
  {
    if (this.taskForm.get('urgentPriority').value === true)
    {
      this.taskForm.controls.priority.setValue(this.listPriorities[0]);
    }
    else
    {
      this.taskForm.controls.priority.setValue(this.listPriorities[1]);
    }
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      if (this.selectedFiles.find(f => f.name === file.name) !== undefined)
      {
        this.messageHandlerService.displayError('FILE-ALREADY-ATTACHED');
        return;
      }

      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      /*
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });*/

      this.taskForm.markAsDirty();
      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;
      this.setFileFromBlob(customFile, file.rawFile);
      this.selectedFiles.push(customFile);
    });
  }

  public deleteAttachment(name: string): void
  {
    this.selectedFiles = this.selectedFiles.filter(f => f.name !== name);
  }

  /**
   * Select attachment and open the uploader.
   */
  public selectAttachment(): void
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  /**
   * Return the proper class to used based on the file mime type.
   *
   * @param mimeType Mime Type of the file.
   * @returns String
   */
  public getFileTypeClass(mimeType: string): string
  {
    return this.documentService.getFileTypeClass(mimeType);
  }

  /**
   * Filter handler for assessment dropdown.
   *
   * @param value User input.
   */
  public assessmentFilter(value: string): void
  {
    this.searchTerm = value;
    this.assessmentsFiltered = sortBy(this.assessments.filter(p => p.name.toLowerCase().includes(value.toLowerCase())), ['name']);
  }

  /**
   * Get all assessments
   */
  private getAssessments(): void
  {
    if (this.listSubscription === undefined)
    {
      this.listSubscription = this.assessmentListService.subscribe({
        next: (value) =>
        {
          if (value !== null)
          {
            const assessments = cloneDeep(value.data).filter(x => x.sensitivities.length === 0);
            assessments.forEach(d => { d.name = d.name + (d.assessmentVersion ? ' - v' + d.assessmentVersion : ''); });
            this.assessments = assessments;
            this.assessmentFilter('');
          }
        }
      });
    }

    const state = {
      filter: {
        filters: [
          { field: 'consentType', operator: 'eq', value: false },
          { field: 'deleted', operator: 'eq', value: false }], logic: 'and'
      },
      group: [],
      skip: 0,
      sort: [{ field: 'name', dir: 'asc' }],
      take: 99999
    };
    this.assessmentListService.queryByState(state);
  }

  /**
   * Get the user linked with the member
   */
  private findUserByMemberOrCase(): void
  {
    if (this.memberId !== null)
    {
      this.userService.getUserByMember(this.memberId).subscribe({
        next: (data) =>
        {
          if (data !== null && this.assigneeUsers.findIndex(x => x.id === data.id) === -1)
          {
            this.assigneeUsers.push(data);
            this.filterAssigneeUsers('');
          }
        }
      });
    }
    else if (this.caseId !== null)
    {
      this.userService.getCacheUserByCase(this.caseId).subscribe({
        next: (data) =>
        {
          if (data !== null && this.assigneeUsers.findIndex(x => x.id === data.id) === -1)
          {
            this.assigneeUsers.push(data);
            this.filterAssigneeUsers('');
          }
        }
      });
    }
  }

  private getTaskStaff(): void
  {
    if (this.channelId > 0)
    {
      // Get Channel is called here just to check for security reasons.
      this.channelService.getCacheChannel(this.channelId).subscribe({
        next: () =>
        {
          this.userService.getCacheUsersByChannelGroups(this.channelId).subscribe({
            next: (users) =>
            {
              this.assigneeUsers = users;
              this.filterAssigneeUsers('');
              this.findUserByMemberOrCase();
            }
          });
          this.setDefaultAssignee();
        }
      });
    }
    // Ad hoc-task
    else
    {
      this.userService.getCacheUsersByRole('ROLE_CARE_COORDINATOR').subscribe({
        next: (users) =>
        {
          this.assigneeUsers = users;
          this.filterAssigneeUsers('');
          this.findUserByMemberOrCase();
        }
      });
      this.setDefaultAssignee();
    }
  }

  /**
   * Set the default assignee id provided if any.
   */
  private setDefaultAssignee(): void
  {
    // Return if task form not created yet
    if (this.taskForm === undefined)
    {
      return;
    }

    if (this.assigneeId !== undefined && this.assigneeId !== null && !isNaN(this.assigneeId))
    {
      this.userService.getUserById(this.assigneeId, false).subscribe({
        next: (user) =>
        {
          // Must not set assigneeUser in case of overrides allowed.
          // The dropdown user selection will decide which assignee to use.
          if (user.deleted !== true)
          {
            if (this.allowAssigneeOverride)
            {
              this.assigneeUsers = [];
              this.assigneeUsers.push(user);
              this.filterAssigneeUsers('');
            }
            this.assignee.setValue({ name: user.fullName, username: user.username, enabled: user.enabled });
          }
        }
      });
    }
  }

  private buildForm(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.attachmentsEnabled = (sessionSetting.attachmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.attachmentsRoles) : false;

        const priorities = sessionSetting.taskPriorities.split(',');
        this.listPriorities = this.listPriorities.filter(priority => priorities.includes(priority.id));
        this.listPriorities.sort((a, b) => (a.value > b.value) ? -1 : 1);
        this.maxOpenedOn = new Date();
        this.taskForm = this.formBuilder.group({
          id: null,
          name: [null, Validators.required],
          description: [null],
          note: [null],
          openedOn: [this.maxOpenedOn, Validators.required],
          dueDate: null,
          priority: [null, this.listPriorities.length > 2 ? Validators.required : []],
          urgentPriority: [false],
          assignee: [null, Validators.required],
          assessment: [null]
        });
      }
    });
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(customFile: CustomFile, file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      const base64: any = reader.result;
      customFile.data = base64.split('base64,')[1];
    }, false);
    reader.readAsDataURL(file);
  }
}
